import React, { useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getMyRooms, deleteRoom } from '../api/rooms'
import { useAuth } from '../context/AuthContext'
import LandlordCard from '../components/LandlordCard'

export default function Bailleurs() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const sliderRef = useRef(null)

  const [rooms, setRooms] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState(null)

  const load = React.useCallback(async () => {
    if (!user) return
    setLoading(true); setErr(null)
    try {
      const list = await getMyRooms(user.id || user.userId)
      setRooms(list)
    } catch (e) {
      console.error(e); setErr('Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [user])

  React.useEffect(() => { load() }, [load])

  const scroll = (direction) => {
    if (sliderRef.current) {
      const { current } = sliderRef
      const scrollAmount = direction === 'left' ? -350 : 350
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  const onEdit = (id) => navigate(`/bailleurs/${id}/edit`)
  const onDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return
    try { await deleteRoom(id); await load() }
    catch (e) { console.error(e); alert('Suppression impossible') }
  }

  if (!user) {
    return (
      <section className="section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="section-title">Espace Bailleurs</h2>
          <p className="text-muted">Veuillez vous connecter pour voir vos annonces.</p>
          <Link to="/login" className="btn btn-primary mt-4">Se connecter</Link>
        </div>
      </section>
    )
  }

  return (
    <section className="section bg-gray-50 min-h-[80vh]">
      <div className="container mx-auto px-4">
        
        {/* En-tête simplifié : Titre et Bouton Ajouter uniquement */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="section-title text-3xl mb-1" dangerouslySetInnerHTML={{ __html: t('landlords.title', 'Mes Annonces') }} />
            <p className="text-gray-500 text-sm">Gérez vos biens immobiliers en toute simplicité.</p>
          </div>
          
          <Link to="/bailleurs/nouveau" className="btn btn-primary flex items-center gap-2 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
            <span className="text-xl">+</span> {t('landlord.addNew', 'Ajouter un bien')}
          </Link>
        </div>

        {err && <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

        {loading ? (
          <div className="text-center py-10 text-gray-500">Chargement de vos biens...</div>
        ) : (
          <>
            {rooms.length > 0 ? (
              
              /* CONTENEUR RELATIF pour positionner les flèches */
              <div className="relative group">

                {/* --- BOUTON GAUCHE --- */}
                {/* Il est "absolute", centré verticalement (top-1/2), et légèrement décalé à gauche */}
                <button 
                  onClick={() => scroll('left')} 
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 bg-white hover:bg-gray-50 text-gray-800 rounded-full p-3 shadow-lg border border-gray-100 transition-all hover:scale-110 hidden md:flex items-center justify-center"
                  aria-label="Précédent"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </button>

                {/* --- ZONE DE DÉFILEMENT (CAROUSEL) --- */}
                <div 
                  ref={sliderRef}
                  className="
                    flex 
                    gap-6 
                    overflow-x-auto 
                    pb-8 
                    pt-4
                    px-1
                    snap-x 
                    snap-mandatory 
                    scrollbar-hide
                  "
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {rooms.map(it => (
                    <div key={it.id ?? it.roomID} className="min-w-[300px] md:min-w-[350px] snap-start flex-shrink-0">
                      <LandlordCard 
                        item={it} 
                        onEdit={onEdit} 
                        onDelete={onDelete} 
                      />
                    </div>
                  ))}
                </div>

                {/* --- BOUTON DROITE --- */}
                <button 
                  onClick={() => scroll('right')} 
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 bg-white hover:bg-gray-50 text-gray-800 rounded-full p-3 shadow-lg border border-gray-100 transition-all hover:scale-110 hidden md:flex items-center justify-center"
                  aria-label="Suivant"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>

              </div>

            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
                <p className="text-gray-500 text-lg mb-4">Vous n'avez publié aucune annonce pour le moment.</p>
                <Link to="/bailleurs/nouveau" className="text-blue-600 font-semibold hover:underline">
                  Commencer maintenant →
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}